import React, { useEffect, useState, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Sidebar } from "primereact/sidebar";
import { IconTextButton } from "../../components/ButtonComponents";
import { X, Copy, RefreshCw, Server, AlertCircle, Info, Database } from "lucide-react";
import { toast } from "react-toastify";
import { Firm } from "../../types/firm";
import { useEdgeAgentStore } from "../../store/edgeAgentStore";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  firm: Firm | null;
  onSaveConfig: () => void;
}

const extractParams = (...sqls: (string | undefined)[]) => {
  const sql = sqls.filter(Boolean).join(" ");
  const params = new Set<string>();

  // 1. Yaklaşım: @parametre
  const regex = /@([a-zA-Z0-9_]+)/g;
  let match;
  while ((match = regex.exec(sql)) !== null) {
    params.add(match[1]);
  }

  // 2. Yaklaşım: INSERT INTO Tablo (col1, col2) -> otomatik sütun yakalama
  const insertRegex = /insert\s+into\s+[a-zA-Z0-9_]+\s*\(([^)]+)\)/ig;
  let insertMatch;
  while ((insertMatch = insertRegex.exec(sql)) !== null) {
    if (insertMatch[1]) {
      const cols = insertMatch[1].split(',').map(c => c.trim().replace(/[[\]'"`]/g, ''));
      cols.forEach(c => {
        if (c && !params.has(c)) {
          params.add(c);
        }
      });
    }
  }

  return Array.from(params);
};

const mappingTypeOptions = [
  { label: "Seçiniz...", value: "" },
  { label: "Plasiyo Modeli", value: "MODEL" },
  { label: "Statik Değer", value: "STATIC" },
  { label: "NULL", value: "NULL" },
];

export const FirmEdgeAgentDialog: React.FC<Props> = ({ isOpen, onClose, firm, onSaveConfig }) => {
  const { logs, fetchLogs, regenerateKey, updateConfig, loading, integrations, activeTemplates, fetchIntegrations, fetchIntegrationTemplates, schemaDictionary, fetchSchemaDictionary, createIntegrationTemplate } = useEdgeAgentStore();

  const [localFirm, setLocalFirm] = useState<Firm | null>(null);

  // States
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);
  const [readQueries, setReadQueries] = useState<Record<string, { deltaSyncSQL?: string; detailSyncSQL?: string; deleteCheckSQL?: string; isActive?: boolean }>>({});
  const [writeQueries, setWriteQueries] = useState<Record<string, {
    isActive?: boolean;
    header?: { insertSQL?: string; updateSQL?: string; preSQL?: string; mapping?: Record<string, string> };
    lines?: { insertSQL?: string; updateSQL?: string; preSQL?: string; mapping?: Record<string, string> };
  }>>({});
  const [activeModuleIndex, setActiveModuleIndex] = useState<number>(0);
  const [isAddTemplateOpen, setIsAddTemplateOpen] = useState(false);
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);
  const [newTemplateData, setNewTemplateData] = useState({ name: '', targetEndpoint: '' });

  const [isSchemaSidebarOpen, setIsSchemaSidebarOpen] = useState(false);
  const [schemaSearchTerm, setSchemaSearchTerm] = useState("");

  const lastActiveTextareaRef = useRef<{ templateName: string, section: 'header' | 'lines', field: 'preSQL' | 'insertSQL' | 'updateSQL', el: HTMLTextAreaElement } | null>(null);

  useEffect(() => {
    fetchIntegrations();
    fetchSchemaDictionary();
  }, [fetchIntegrations, fetchSchemaDictionary]);

  useEffect(() => {
    if (isOpen && firm) {
      setLocalFirm(firm);
      fetchLogs(firm.id);

      const readConf = typeof firm.syncReadConfig === 'string' ? JSON.parse(firm.syncReadConfig) : (firm.syncReadConfig || {});
      const writeConf = typeof firm.syncWriteConfig === 'string' ? JSON.parse(firm.syncWriteConfig) : (firm.syncWriteConfig || {});

      const appId = readConf.appId || firm.integrationAppId || null;
      setSelectedAppId(appId);
      if (appId) {
        fetchIntegrationTemplates(appId);
      }

      setReadQueries(readConf.queries || {});
      setWriteQueries(writeConf.queries || {});
      setActiveModuleIndex(0);
    }
  }, [isOpen, firm, fetchLogs, fetchIntegrationTemplates]);

  // Otomatik Eşleştirme (Auto-Match)
  useEffect(() => {
    if (!schemaDictionary || schemaDictionary.length === 0) return;

    setWriteQueries(prev => {
      let hasChanges = false;
      const newQueries = { ...prev };

      Object.keys(newQueries).forEach(tName => {
        const template = newQueries[tName];

        (['header', 'lines'] as const).forEach((section) => {
          if (!template[section]) return;

          const hInsertSQL = template[section]?.insertSQL || "";
          const hUpdateSQL = template[section]?.updateSQL || "";
          const params = extractParams(hInsertSQL, hUpdateSQL);

          if (params.length > 0) {
            const currentMapping = { ...(template[section]?.mapping || {}) };
            let sectionChanged = false;

            params.forEach(p => {
              if (!currentMapping[p]) {
                const lowerCol = p.toLowerCase().replace(/_/g, '');
                let bestMatch = "";
                for (const group of schemaDictionary) {
                  for (const item of group.items) {
                    const valLower = item.value.toLowerCase().replace(/_/g, '').replace(/\./g, '');
                    if (valLower.includes(lowerCol) || lowerCol.includes(valLower)) {
                      bestMatch = item.value;
                      break;
                    }
                  }
                  if (bestMatch) break;
                }

                if (bestMatch) {
                  currentMapping[p] = `MODEL:${bestMatch}`;
                  sectionChanged = true;
                }
              }
            });

            if (sectionChanged) {
              newQueries[tName][section]!.mapping = currentMapping;
              hasChanges = true;
            }
          }
        });
      });

      return hasChanges ? newQueries : prev;
    });
  }, [writeQueries, schemaDictionary]);

  const handleAppChange = (appId: number) => {
    setSelectedAppId(appId);
    if (appId) {
      fetchIntegrationTemplates(appId);
    }
  };

  const handleToggleModuleActive = (templateName: string, active: boolean) => {
    setReadQueries(prev => ({
      ...prev,
      [templateName]: {
        ...(prev[templateName] || {}),
        isActive: active
      }
    }));
    setWriteQueries(prev => ({
      ...prev,
      [templateName]: {
        ...(prev[templateName] || {}),
        isActive: active
      }
    }));
  };

  const handleReadQueryChange = (templateName: string, field: 'deltaSyncSQL' | 'detailSyncSQL' | 'deleteCheckSQL', value: string) => {
    setReadQueries(prev => ({
      ...prev,
      [templateName]: {
        ...(prev[templateName] || {}),
        [field]: value
      }
    }));
  };

  const handleWriteQueryChange = (templateName: string, section: 'header' | 'lines', field: 'insertSQL' | 'updateSQL' | 'preSQL', value: string) => {
    setWriteQueries(prev => ({
      ...prev,
      [templateName]: {
        ...(prev[templateName] || {}),
        [section]: {
          ...(prev[templateName]?.[section] || {}),
          [field]: value
        }
      }
    }));
  };

  const handleWriteMappingChange = (templateName: string, section: 'header' | 'lines', paramName: string, newType: string, newValue: string) => {
    let finalVal = "";
    if (newType === "NULL") finalVal = "NULL";
    else if (newType === "MODEL") finalVal = `MODEL:${newValue}`;
    else if (newType === "STATIC") finalVal = `STATIC:${newValue}`;

    setWriteQueries(prev => ({
      ...prev,
      [templateName]: {
        ...(prev[templateName] || {}),
        [section]: {
          ...(prev[templateName]?.[section] || {}),
          mapping: {
            ...(prev[templateName]?.[section]?.mapping || {}),
            [paramName]: finalVal
          }
        }
      }
    }));
  };

  const handleInjectSchema = (schemaValue: string) => {
    if (!lastActiveTextareaRef.current) {
      toast.info("Lütfen önce SQL yazılacak metin kutusuna tıklayın.");
      return;
    }

    const { templateName, section, field, el } = lastActiveTextareaRef.current;
    const paramName = schemaValue.replace(/\./g, '_');
    const injection = `@${paramName}`;

    const start = el.selectionStart;
    const end = el.selectionEnd;

    const currentVal = writeQueries[templateName]?.[section]?.[field] || "";
    const newVal = currentVal.substring(0, start) + injection + currentVal.substring(end);

    handleWriteQueryChange(templateName, section, field, newVal);
    handleWriteMappingChange(templateName, section, paramName, "MODEL", schemaValue);

    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + injection.length, start + injection.length);
    }, 0);
  };

  const parseMapping = (val: string) => {
    if (!val) return { type: '', value: '' };
    if (val === 'NULL') return { type: 'NULL', value: '' };
    if (val.startsWith('MODEL:')) return { type: 'MODEL', value: val.substring(6) };
    if (val.startsWith('STATIC:')) return { type: 'STATIC', value: val.substring(7) };
    return { type: '', value: '' };
  };

  const copyToClipboard = () => {
    if (localFirm?.agentSecretKey) {
      navigator.clipboard.writeText(localFirm.agentSecretKey);
      toast.info("Gizli anahtar kopyalandı!");
    }
  };

  const handleRegenerateKey = async () => {
    if (!localFirm) return;
    if (window.confirm("Yeni anahtar üretmek, mevcut ajanların bağlantısını koparacaktır. Emin misiniz?")) {
      const newKey = await regenerateKey(localFirm.id);
      if (newKey) {
        setLocalFirm({ ...localFirm, agentSecretKey: newKey });
        onSaveConfig();
      }
    }
  };

  const validateMappings = () => {
    for (const t of activeTemplates) {
      const tName = t.name;
      const hParams = extractParams(writeQueries[tName]?.header?.insertSQL, writeQueries[tName]?.header?.updateSQL, writeQueries[tName]?.header?.preSQL);
      const lParams = extractParams(writeQueries[tName]?.lines?.insertSQL, writeQueries[tName]?.lines?.updateSQL, writeQueries[tName]?.lines?.preSQL);

      const hMapping = writeQueries[tName]?.header?.mapping || {};
      const lMapping = writeQueries[tName]?.lines?.mapping || {};

      for (const p of hParams) {
        if (!hMapping[p] || parseMapping(hMapping[p]).type === '') {
          toast.warn(`Hata: ${tName} -> Ana Kayıt (Header) -> ${p} parametresi eşleştirilmemiş!`);
          return false;
        }
      }
      for (const p of lParams) {
        if (!lMapping[p] || parseMapping(lMapping[p]).type === '') {
          toast.warn(`Hata: ${tName} -> Alt Kalemler (Lines) -> ${p} parametresi eşleştirilmemiş!`);
          return false;
        }
      }
    }
    return true;
  };

  const enhanceInsertSQL = (sql: string) => {
    if (!sql) return sql;
    const insertMatch = sql.match(/insert\s+into\s+[a-zA-Z0-9_]+\s*\(([^)]+)\)/i);
    if (insertMatch && !sql.toLowerCase().includes('values')) {
      const cols = insertMatch[1].split(',').map(c => c.trim().replace(/[[\]'"`]/g, ''));
      if (cols.length > 0) {
        const valuesStr = "\nVALUES (" + cols.map(c => `@${c}`).join(', ') + ")";
        return sql.trim() + valuesStr;
      }
    }
    return sql;
  };

  const previewData = React.useMemo(() => {
    const enhancedReadQueries = { ...readQueries };
    activeTemplates.forEach(t => {
      const tName = t.name;
      if (!enhancedReadQueries[tName]) enhancedReadQueries[tName] = {};
      if (enhancedReadQueries[tName].deltaSyncSQL === undefined) {
        enhancedReadQueries[tName].deltaSyncSQL = t.templateSQL || "";
      }
    });

    const payloadRead = {
      appId: selectedAppId,
      queries: enhancedReadQueries
    };

    const enhancedWriteQueries = Object.keys(writeQueries).reduce((acc, tName) => {
      const t = writeQueries[tName];
      acc[tName] = {
        isActive: t.isActive,
        header: t.header ? {
          ...t.header,
          insertSQL: enhanceInsertSQL(t.header.insertSQL || "")
        } : undefined,
        lines: t.lines ? {
          ...t.lines,
          insertSQL: enhanceInsertSQL(t.lines.insertSQL || "")
        } : undefined
      };
      return acc;
    }, {} as any);

    const payloadWrite = {
      queries: enhancedWriteQueries
    };

    return { payloadRead, payloadWrite };
  }, [readQueries, writeQueries, selectedAppId, activeTemplates]);

  const handleSaveConfigs = async () => {
    if (!localFirm || !selectedAppId) {
      toast.warn("Lütfen bir Entegrasyon Türü (ERP) seçin.");
      return;
    }

    if (!validateMappings()) return;

    // Görünen ama kaydedilmeyen default şablonları da otomatik ekleyelim
    const enhancedReadQueries = { ...readQueries };
    activeTemplates.forEach(t => {
      const tName = t.name;
      if (!enhancedReadQueries[tName]) enhancedReadQueries[tName] = {};
      if (enhancedReadQueries[tName].deltaSyncSQL === undefined) {
        enhancedReadQueries[tName].deltaSyncSQL = t.templateSQL || "";
      }
    });

    const payloadRead = {
      appId: selectedAppId,
      queries: enhancedReadQueries
    };

    const enhancedWriteQueries = Object.keys(writeQueries).reduce((acc, tName) => {
      const t = writeQueries[tName];
      acc[tName] = {
        isActive: t.isActive,
        header: t.header ? {
          ...t.header,
          insertSQL: enhanceInsertSQL(t.header.insertSQL || "")
        } : undefined,
        lines: t.lines ? {
          ...t.lines,
          insertSQL: enhanceInsertSQL(t.lines.insertSQL || "")
        } : undefined
      };
      return acc;
    }, {} as any);

    const payloadWrite = {
      queries: enhancedWriteQueries
    };

    try {
      const result = await updateConfig(localFirm.id, {
        syncReadConfig: payloadRead,
        syncWriteConfig: payloadWrite,
      });
      if (result) {
        setLocalFirm({ ...localFirm, syncReadConfig: result.syncReadConfig, syncWriteConfig: result.syncWriteConfig });
        onSaveConfig();
      }
    } catch (e) {
      toast.error("Ayarlar kaydedilirken hata oluştu.");
    }
  };

  const renderStatusBadge = () => {
    const status = localFirm?.agentStatus || "OFFLINE";
    let color = "bg-secondary";
    let label = "Bilinmiyor";

    if (status === "RUNNING") { color = "bg-success"; label = "Çalışıyor"; }
    else if (status === "STOPPED") { color = "bg-secondary"; label = "Durduruldu"; }
    else if (status === "OFFLINE" || status === "ERROR") { color = "bg-danger"; label = status === "OFFLINE" ? "Çevrimdışı" : "Hata"; }

    return (
      <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-white text-xs font-bold ${color}`}>
        <Server size={14} />
        {label}
      </div>
    );
  };

  const renderMappingTable = (templateName: string, section: 'header' | 'lines', params: string[], mapping: Record<string, string>) => {
    if (params.length === 0) return null;

    return (
      <div className="mt-4 p-3 bg-card border border-borderColor rounded-lg shadow-sm">
        <h5 className="text-xs font-bold text-text mb-3 border-b border-borderColor pb-2 flex items-center gap-2">
          <Database size={14} className="text-primary" /> Otomatik Eşleştirme Tablosu
        </h5>
        <DataTable value={params.map(p => ({ param: p, mappedTo: mapping[p] || "" }))} size="small" className="p-datatable-sm">
          <Column field="param" header="ERP Parametresi" body={(row) => (
            <span className="px-2 py-1 bg-slate-100 border text-slate-800 font-mono text-xs rounded-full font-bold">@{row.param}</span>
          )} style={{ width: '25%' }} />

          <Column header="Kaynak (Source)" body={(row) => {
            const { type, value } = parseMapping(row.mappedTo);
            return (
              <Dropdown
                value={type}
                options={mappingTypeOptions}
                onChange={(e) => handleWriteMappingChange(templateName, section, row.param, e.value, value)}
                className="w-full p-inputtext-sm"
              />
            );
          }} style={{ width: '25%' }} />

          <Column header="Değer / Eşleşme" body={(row) => {
            const { type, value } = parseMapping(row.mappedTo);
            if (type === "MODEL") {
              return (
                <Dropdown
                  value={value}
                  options={schemaDictionary}
                  optionGroupLabel="label"
                  optionGroupChildren="items"
                  onChange={(e) => handleWriteMappingChange(templateName, section, row.param, type, e.value)}
                  placeholder="Şemadan Alan Seçiniz"
                  className="w-full p-inputtext-sm"
                  filter
                />
              );
            }
            if (type === "STATIC") {
              return (
                <InputText
                  value={value}
                  onChange={(e) => handleWriteMappingChange(templateName, section, row.param, type, e.target.value)}
                  placeholder="Statik değer giriniz"
                  className="w-full p-inputtext-sm"
                />
              );
            }
            if (type === "NULL") {
              return <span className="text-xs text-slate-400 italic bg-slate-50 px-2 py-1 rounded">Boş (NULL) geçilecek</span>;
            }
            return <span className="text-xs text-red-500 italic">Lütfen kaynak seçin!</span>;
          }} />
        </DataTable>
      </div>
    );
  };

  const getRelativeTime = (dateString?: string | null) => {
    if (!dateString) return "Hiç sinyal alınmadı";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    if (diffMs < 60000) return "Az önce";
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins} dakika önce`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} saat önce`;
    return `${Math.floor(diffHours / 24)} gün önce`;
  };

  return (<>
    <Dialog
      header={`${firm?.name || "Firma"} - Edge Agent Yönetimi`}
      visible={isOpen}
      onHide={onClose}
      maximized
      modal
      footer={
        <div className="flex justify-between w-full">
          <div className="text-xs text-slate-500 flex items-center gap-1"><Database size={14} /> Edge Agent v5 - NoCode Platform</div>
          <div className="flex gap-2">
            <IconTextButton onClick={onClose} icon={X} className="btn-cancel">Kapat</IconTextButton>
            <IconTextButton onClick={handleSaveConfigs} icon={RefreshCw} className="btn-new" disabled={loading}>Ayarları Kaydet</IconTextButton>
          </div>
        </div>
      }
    >
      <TabView className="pt-2">
        {/* TAB 1: BAĞLANTI & DURUM */}
        <TabPanel header="Bağlantı & Durum">
          <div className="space-y-6 mt-4">

            <div className="flex justify-between items-center bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div>
                <h4 className="text-sm font-bold text-slate-700">Agent Durumu</h4>
                <p className="text-xs text-slate-500 mt-1">Son Sinyal: {getRelativeTime(localFirm?.agentLastHeartbeat)}</p>
              </div>
              {renderStatusBadge()}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700">Gizli Anahtar (Secret Key)</label>
              <div className="flex gap-2">
                <InputText
                  value={localFirm?.agentSecretKey ? "••••••••••••" + localFirm.agentSecretKey.slice(-4) : "Anahtar atanmamış"}
                  readOnly
                  className="w-full bg-slate-100"
                />
                <IconTextButton onClick={copyToClipboard} icon={Copy} className="btn-light" disabled={!localFirm?.agentSecretKey}>
                  Kopyala
                </IconTextButton>
              </div>
              <div className="pt-2">
                <IconTextButton onClick={handleRegenerateKey} icon={RefreshCw} className="btn-new text-xs">
                  Yeni Anahtar Üret / Sıfırla
                </IconTextButton>
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-4 border-t border-slate-200">
              <label className="text-sm font-bold text-slate-700">Entegrasyon Türü (ERP)</label>
              <Dropdown
                value={selectedAppId}
                options={integrations}
                optionLabel="name"
                optionValue="id"
                onChange={(e) => handleAppChange(e.value)}
                placeholder="Bir ERP seçiniz"
                className="w-full md:w-1/2"
                filter
              />
            </div>
          </div>
        </TabPanel>

        {/* TAB 2: MODÜLLER VE EŞLEŞTİRME (MASTER-DETAIL) */}
        <TabPanel header="Modüller (Visual Mapper)" disabled={!selectedAppId || activeTemplates.length === 0}>
          <div className="mt-4 flex flex-col border border-slate-200 rounded-xl overflow-hidden min-h-[600px] bg-slate-50">

            {/* ÜST MENÜ (Horizontal Modules) */}
            <div className="flex justify-between items-center bg-slate-100 border-b border-slate-300 p-2 overflow-x-auto gap-4">
              <div className="flex gap-2 items-center flex-nowrap shrink-0">
                <span className="text-xs font-bold text-slate-500 uppercase px-2 shrink-0">Modüller:</span>
                {activeTemplates.map((template, idx) => (
                  <button
                    key={template.id}
                    className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all duration-200 shrink-0 ${activeModuleIndex === idx
                      ? 'bg-blue-600 text-white shadow'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                      }`}
                    onClick={() => setActiveModuleIndex(idx)}
                  >
                    {template.name}
                  </button>
                ))}

                <button
                  onClick={() => setIsAddTemplateOpen(true)}
                  className="px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-md text-xs font-bold transition-colors shrink-0 whitespace-nowrap"
                >
                  + Yeni Ekle
                </button>
              </div>

              <div className="shrink-0 px-2">
                <IconTextButton
                  icon={Database}
                  onClick={() => setIsSchemaSidebarOpen(true)}
                  className="px-3 py-1.5 bg-bgAlt hover:bg-dropdownHover text-primary border border-borderColor rounded-md text-xs font-bold transition-colors flex items-center gap-1 shrink-0"
                >
                  Şema Sözlüğü (DTO)
                </IconTextButton>
              </div>
            </div>

            {/* İÇERİK (Tam Genişlik) */}
            <div className="flex-1 bg-white flex flex-col overflow-hidden w-full">
              {activeTemplates[activeModuleIndex] ? (() => {
                const template = activeTemplates[activeModuleIndex];
                const tName = template.name;

                // Read Logic
                const deltaSQL = readQueries[tName]?.deltaSyncSQL !== undefined ? readQueries[tName].deltaSyncSQL : (template.templateSQL || "");
                const deleteSQL = readQueries[tName]?.deleteCheckSQL || "";

                // Write Logic (Header)
                const hPreSQL = writeQueries[tName]?.header?.preSQL || "";
                const hInsertSQL = writeQueries[tName]?.header?.insertSQL || "";
                const hUpdateSQL = writeQueries[tName]?.header?.updateSQL || "";
                const hMapping = writeQueries[tName]?.header?.mapping || {};
                const hParams = extractParams(hPreSQL, hInsertSQL, hUpdateSQL);

                // Write Logic (Lines)
                const lPreSQL = writeQueries[tName]?.lines?.preSQL || "";
                const lInsertSQL = writeQueries[tName]?.lines?.insertSQL || "";
                const lUpdateSQL = writeQueries[tName]?.lines?.updateSQL || "";
                const lMapping = writeQueries[tName]?.lines?.mapping || {};
                const lParams = extractParams(lPreSQL, lInsertSQL, lUpdateSQL);

                return (
                  <div className="flex-1 flex flex-col h-full">
                    <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-white">
                      <h3 className="text-lg font-bold text-slate-800">{template.name} <span className="text-sm font-normal text-slate-500">({template.targetEndpoint})</span></h3>
                      <div className="flex items-center gap-3 bg-slate-50 border px-3 py-1.5 rounded-lg shadow-sm">
                        <label className="text-xs font-bold text-slate-600 cursor-pointer" htmlFor="moduleActive">Modül Aktif</label>
                        <InputSwitch
                          inputId="moduleActive"
                          checked={readQueries[tName]?.isActive !== false}
                          onChange={(e) => handleToggleModuleActive(tName, e.value)}
                        />
                      </div>
                    </div>

                    <div className={`flex-1 overflow-y-auto p-4 transition-opacity ${readQueries[tName]?.isActive === false ? 'opacity-50 pointer-events-none' : ''}`}>
                      <TabView>

                        {/* OKUMA AYARLARI */}
                        <TabPanel header="Okuma Ayarları (ERP -> Plasiyo)">
                          <div className="w-full space-y-6">
                            <div className="col-span-2">
                              <div className="flex items-start gap-3 bg-bgAlt border border-borderColor p-3 rounded-lg text-primary">
                                <Info size={20} className="mt-0.5 flex-shrink-0 text-primary" />
                                <p className="text-xs leading-relaxed font-medium">
                                  Sadece değişenleri çekmek için <code className="bg-card px-1 py-0.5 rounded text-primary font-bold border border-borderColor">{`{{LAST_SYNC_DATE}}`}</code> kullanabilirsiniz. Sütunlarınızı API'nin beklediği DTO formatına <strong>AS alias</strong> (Örn: `cari_kod AS externalId`) ile eşlemelisiniz (Sağ üstten Şema Sözlüğüne bakabilirsiniz).
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                              {/* 1. Delta Sync SQL */}
                              <div className="space-y-2 flex flex-col h-full">
                                <div className="flex justify-between items-end">
                                  <label className="text-xs font-bold text-slate-700">Delta Senkronizasyon Sorgusu (Ana Tablo / Header)</label>
                                  <button
                                    onClick={() => {
                                      handleReadQueryChange(tName, 'deltaSyncSQL', template.templateSQL || "");
                                      if (template.detailTemplateSQL) {
                                        handleReadQueryChange(tName, 'detailSyncSQL', template.detailTemplateSQL);
                                      }
                                    }}
                                    className="text-[10px] bg-bgAlt hover:bg-dropdownHover text-primary px-2 py-1 rounded transition-colors font-bold flex items-center gap-1 border border-borderColor"
                                  >
                                    <Copy size={12} /> Ana Şablondan Kopyala
                                  </button>
                                </div>
                                <InputTextarea
                                  value={deltaSQL}
                                  onChange={(e) => handleReadQueryChange(tName, 'deltaSyncSQL', e.target.value)}
                                  className="w-full font-mono text-sm leading-relaxed p-3 bg-slate-900 text-green-400 border-none rounded-lg shadow-inner focus:ring-2 focus:ring-indigo-500 flex-1 min-h-[250px]"
                                  placeholder="SELECT * FROM SİPARİŞLER WHERE GUNCELLEME_TARIHI >= '{{LAST_SYNC_DATE}}'"
                                  spellCheck={false}
                                />
                              </div>

                              {/* 2. Detail Sync SQL */}
                              <div className="space-y-2 flex flex-col h-full">
                                <div className="flex justify-between items-end">
                                  <label className="text-xs font-bold text-slate-700">Alt Kalem Sorgusu (Detail Sync SQL)</label>
                                </div>
                                <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mb-2 rounded text-xs text-amber-900 flex items-start gap-2 shadow-sm">
                                  <Info size={16} className="mt-0.5 shrink-0 text-amber-600" />
                                  <div className="space-y-1.5">
                                    <p className="font-bold">Detay Sorgusu Değişkenleri:</p>
                                    <ul className="list-disc list-inside ml-1 space-y-1 mt-1 text-[11px]">
                                      <li><strong>IN ($ParentIds)</strong> : Normal ID'ler için.</li>
                                      <li><strong>IN ($ParentGuid)</strong> : GUID tipi kolonlar için.</li>
                                    </ul>
                                  </div>
                                </div>
                                <InputTextarea
                                  value={readQueries[tName]?.detailSyncSQL || ""}
                                  onChange={(e) => handleReadQueryChange(tName, 'detailSyncSQL', e.target.value)}
                                  className="w-full font-mono text-sm leading-relaxed p-3 bg-slate-900 text-green-400 border-none rounded-lg shadow-inner focus:ring-2 focus:ring-amber-500 flex-1 min-h-[250px]"
                                  placeholder="SELECT * FROM SIPARIS_KALEMLERI WHERE SIPARIS_ID IN ($ParentIds)"
                                  spellCheck={false}
                                />
                              </div>

                              {/* 3. Delete Check SQL */}
                              <div className="space-y-2 flex flex-col h-full">
                                <label className="text-xs font-bold text-slate-700">Silinme Kontrol Sorgusu (ID List Fetch) - <span className="text-slate-400 font-normal italic">Opsiyonel</span></label>
                                <div className="bg-orange-50 border-l-4 border-orange-500 p-3 mb-2 rounded text-[11px] text-orange-900 flex items-start gap-2 shadow-sm">
                                  <Info size={16} className="mt-0.5 shrink-0 text-orange-600" />
                                  <div className="space-y-1.5">
                                    <p>Tüm <strong>aktif</strong> kayıtların ID'lerini seçin. Agent bu listede olmayanları silinmiş sayar.</p>
                                  </div>
                                </div>
                                <InputTextarea
                                  value={deleteSQL}
                                  onChange={(e) => handleReadQueryChange(tName, 'deleteCheckSQL', e.target.value)}
                                  className="w-full font-mono text-xs p-3 bg-slate-900 text-orange-400 rounded-lg shadow-inner flex-1 min-h-[250px]"
                                  placeholder="SELECT ID FROM SİPARİŞLER"
                                  spellCheck={false}
                                />
                              </div>
                            </div>
                          </div>
                        </TabPanel>

                        {/* YAZMA AYARLARI */}
                        <TabPanel header="Yazma Ayarları (Plasiyo -> ERP)">
                          <div className="w-full space-y-6">
                            
                            {/* EŞLEŞTİRME REHBERİ (INFO BOX) */}
                            <div className="col-span-2">
                              <div className="flex items-start gap-3 bg-bgAlt border border-borderColor p-4 rounded-xl text-text shadow-sm">
                                <Info size={24} className="mt-0.5 flex-shrink-0 text-primary" />
                                <div className="flex flex-col gap-1">
                                  <p className="font-bold text-primary">Otomatik Eşleştirme (Mapping) Nasıl Çalışır?</p>
                                  <p className="text-xs leading-relaxed font-medium opacity-90">
                                    SQL sorgularınızdaki <code className="bg-card px-1 py-0.5 rounded border font-bold">@parametre</code> değerleri veya <code className="bg-card px-1 py-0.5 rounded border font-bold">INSERT INTO Tablo (sutun1, sutun2)</code> içindeki sütun adları otomatik olarak çıkarılır ve <strong className="text-primary">API Şema Sözlüğü</strong> ile karşılaştırılarak en uygun alanla (Örn: <code>cari_kod</code> ➔ <code>MODEL:externalId</code>) otomatik eşleştirilir.
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* HEADER */}
                            <div className="bg-slate-50 border border-slate-300 rounded-xl p-4 shadow-sm">
                              <h4 className="text-base font-black text-slate-800 mb-4 border-b border-slate-200 pb-2">Ana Kayıt (Header) İşlemleri</h4>

                              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                  <label className="text-xs font-bold text-blue-700 flex items-center gap-1">
                                    <Database size={14} /> preSQL (Ön Sorgu)
                                  </label>
                                  <InputTextarea
                                    value={hPreSQL}
                                    onClick={(e) => { lastActiveTextareaRef.current = { templateName: tName, section: 'header', field: 'preSQL', el: e.target as HTMLTextAreaElement } }}
                                    onKeyUp={(e) => { lastActiveTextareaRef.current = { templateName: tName, section: 'header', field: 'preSQL', el: e.target as HTMLTextAreaElement } }}
                                    onChange={(e) => handleWriteQueryChange(tName, 'header', 'preSQL', e.target.value)}
                                    rows={5}
                                    className="w-full font-mono text-xs p-3 bg-slate-900 text-blue-300 rounded-lg shadow-inner"
                                    placeholder="SELECT MAX(sira)+1 AS sira FROM..."
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-xs font-bold text-emerald-700">Create (INSERT) SQL</label>
                                  <InputTextarea
                                    value={hInsertSQL}
                                    onClick={(e) => { lastActiveTextareaRef.current = { templateName: tName, section: 'header', field: 'insertSQL', el: e.target as HTMLTextAreaElement } }}
                                    onKeyUp={(e) => { lastActiveTextareaRef.current = { templateName: tName, section: 'header', field: 'insertSQL', el: e.target as HTMLTextAreaElement } }}
                                    onChange={(e) => handleWriteQueryChange(tName, 'header', 'insertSQL', e.target.value)}
                                    rows={5}
                                    className="w-full font-mono text-xs p-3 bg-slate-900 text-emerald-300 rounded-lg shadow-inner"
                                    placeholder="INSERT INTO SIP_BASLIK (tarih, tutar)"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-xs font-bold text-amber-700">Update (UPDATE) SQL</label>
                                  <InputTextarea
                                    value={hUpdateSQL}
                                    onClick={(e) => { lastActiveTextareaRef.current = { templateName: tName, section: 'header', field: 'updateSQL', el: e.target as HTMLTextAreaElement } }}
                                    onKeyUp={(e) => { lastActiveTextareaRef.current = { templateName: tName, section: 'header', field: 'updateSQL', el: e.target as HTMLTextAreaElement } }}
                                    onChange={(e) => handleWriteQueryChange(tName, 'header', 'updateSQL', e.target.value)}
                                    rows={5}
                                    className="w-full font-mono text-xs p-3 bg-slate-900 text-amber-300 rounded-lg shadow-inner"
                                    placeholder="UPDATE SIP_BASLIK SET tutar=@tutar"
                                  />
                                </div>
                              </div>

                              {renderMappingTable(tName, 'header', hParams, hMapping)}
                            </div>

                            {/* LINES */}
                            <div className="bg-slate-50 border border-slate-300 rounded-xl p-4 shadow-sm">
                              <h4 className="text-base font-black text-slate-800 mb-4 border-b border-slate-200 pb-2">Alt Kalemler (Lines) İşlemleri</h4>

                              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                  <label className="text-xs font-bold text-blue-700 flex items-center gap-1">
                                    <Database size={14} /> preSQL (Ön Sorgu)
                                  </label>
                                  <InputTextarea
                                    value={lPreSQL}
                                    onClick={(e) => { lastActiveTextareaRef.current = { templateName: tName, section: 'lines', field: 'preSQL', el: e.target as HTMLTextAreaElement } }}
                                    onKeyUp={(e) => { lastActiveTextareaRef.current = { templateName: tName, section: 'lines', field: 'preSQL', el: e.target as HTMLTextAreaElement } }}
                                    onChange={(e) => handleWriteQueryChange(tName, 'lines', 'preSQL', e.target.value)}
                                    rows={5}
                                    className="w-full font-mono text-xs p-3 bg-slate-900 text-blue-300 rounded-lg shadow-inner"
                                    placeholder="SELECT STOK_KODU FROM..."
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-xs font-bold text-emerald-700">Create (INSERT) SQL</label>
                                  <InputTextarea
                                    value={lInsertSQL}
                                    onClick={(e) => { lastActiveTextareaRef.current = { templateName: tName, section: 'lines', field: 'insertSQL', el: e.target as HTMLTextAreaElement } }}
                                    onKeyUp={(e) => { lastActiveTextareaRef.current = { templateName: tName, section: 'lines', field: 'insertSQL', el: e.target as HTMLTextAreaElement } }}
                                    onChange={(e) => handleWriteQueryChange(tName, 'lines', 'insertSQL', e.target.value)}
                                    rows={5}
                                    className="w-full font-mono text-xs p-3 bg-slate-900 text-emerald-300 rounded-lg shadow-inner"
                                    placeholder="INSERT INTO SIP_KALEM (stok_kodu, miktar)"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-xs font-bold text-amber-700">Update (UPDATE) SQL</label>
                                  <InputTextarea
                                    value={lUpdateSQL}
                                    onClick={(e) => { lastActiveTextareaRef.current = { templateName: tName, section: 'lines', field: 'updateSQL', el: e.target as HTMLTextAreaElement } }}
                                    onKeyUp={(e) => { lastActiveTextareaRef.current = { templateName: tName, section: 'lines', field: 'updateSQL', el: e.target as HTMLTextAreaElement } }}
                                    onChange={(e) => handleWriteQueryChange(tName, 'lines', 'updateSQL', e.target.value)}
                                    rows={5}
                                    className="w-full font-mono text-xs p-3 bg-slate-900 text-amber-300 rounded-lg shadow-inner"
                                    placeholder="UPDATE SIP_KALEM SET miktar=@miktar"
                                  />
                                </div>
                              </div>

                              {renderMappingTable(tName, 'lines', lParams, lMapping)}
                            </div>
                          </div>
                        </TabPanel>

                        {/* JSON PREVIEW */}
                        <TabPanel header="Ham Veri (JSON Önizleme)">
                          <div className="w-full space-y-4">
                            <div className="bg-slate-50 border border-slate-300 rounded-xl p-4 shadow-sm">
                              <h4 className="text-base font-black text-slate-800 mb-4 border-b border-slate-200 pb-2">Sunucuya Gönderilecek JSON Yapısı</h4>
                              <p className="text-xs text-slate-600 mb-4">Bu alan sadece geliştiriciler/kontrol amaçlıdır. "Ayarları Kaydet" butonuna bastığınızda aşağıdaki JSON formatı oluşturulup sunucuya iletilir.</p>
                              
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div>
                                  <label className="text-xs font-bold text-emerald-700 block mb-2">Okuma Ayarları (syncReadConfig)</label>
                                  <pre className="w-full h-96 overflow-auto font-mono text-xs p-3 bg-slate-900 text-emerald-300 rounded-lg shadow-inner whitespace-pre-wrap">
                                    {JSON.stringify(previewData.payloadRead, null, 2)}
                                  </pre>
                                </div>
                                <div>
                                  <label className="text-xs font-bold text-amber-700 block mb-2">Yazma Ayarları (syncWriteConfig)</label>
                                  <pre className="w-full h-96 overflow-auto font-mono text-xs p-3 bg-slate-900 text-amber-300 rounded-lg shadow-inner whitespace-pre-wrap">
                                    {JSON.stringify(previewData.payloadWrite, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </TabPanel>

                      </TabView>
                    </div>
                  </div>
                );
              })() : (
                <div className="flex-1 flex items-center justify-center text-slate-400 font-bold text-sm">
                  Soldan bir modül seçiniz.
                </div>
              )}
            </div>
          </div>
        </TabPanel >

        {/* TAB 3: KRİTİK HATALAR */}
        < TabPanel header="Kritik Hatalar" >
          <div className="mt-4">
            <DataTable
              value={logs}
              paginator
              rows={5}
              loading={loading}
              emptyMessage="Kritik hata kaydı bulunamadı."
              className="p-datatable-sm border rounded"
            >
              <Column
                header="Tarih"
                body={(row) => new Date(row.createdAt).toLocaleString('tr-TR')}
                style={{ width: '20%' }}
              />
              <Column
                header="Seviye"
                body={(row) => (
                  <span className={`px-2 py-1 rounded text-xs font-bold text-white ${row.level === 'FATAL' ? 'bg-red-600' : 'bg-orange-500'}`}>
                    {row.level}
                  </span>
                )}
                style={{ width: '15%' }}
              />
              <Column field="message" header="Mesaj" style={{ width: '50%' }} />
              <Column
                header="Detay"
                body={(row) => (
                  <button
                    onClick={() => {
                      alert(JSON.stringify(row.details, null, 2));
                    }}
                    className="p-2 rounded hover:bg-slate-100 text-primary transition-colors"
                    title="JSON Detayı"
                  >
                    <AlertCircle size={18} />
                  </button>
                )}
                style={{ width: '15%', textAlign: 'center' }}
              />
            </DataTable>
          </div>
        </TabPanel >
      </TabView >
    </Dialog>

    <Dialog
      header="Yeni Modül Ekle"
      visible={isAddTemplateOpen}
      onHide={() => setIsAddTemplateOpen(false)}
      className="w-full max-w-md"
      modal
    >
      <div className="space-y-4 pt-2">
        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700">Modül Adı (örn: Ürünler, Stoklar)</label>
          <InputText
            value={newTemplateData.name}
            onChange={(e) => setNewTemplateData({ ...newTemplateData, name: e.target.value })}
            className="w-full"
            placeholder="Modül Adı"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700">Hedef API Ucu (örn: products, stocks)</label>
          <InputText
            value={newTemplateData.targetEndpoint}
            onChange={(e) => setNewTemplateData({ ...newTemplateData, targetEndpoint: e.target.value.toLowerCase() })}
            className="w-full"
            placeholder="targetEndpoint"
          />
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <button
            onClick={() => setIsAddTemplateOpen(false)}
            className="px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            İptal
          </button>
          <button
            onClick={async () => {
              if (!newTemplateData.name || !newTemplateData.targetEndpoint) {
                toast.error("Lütfen tüm alanları doldurun.");
                return;
              }
              if (!selectedAppId) return;

              setIsAddingTemplate(true);
              const success = await createIntegrationTemplate(selectedAppId, newTemplateData.name, newTemplateData.targetEndpoint);
              setIsAddingTemplate(false);

              if (success) {
                setIsAddTemplateOpen(false);
                setNewTemplateData({ name: '', targetEndpoint: '' });
              }
            }}
            disabled={isAddingTemplate}
            className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
          >
            {isAddingTemplate ? "Ekleniyor..." : "Ekle"}
          </button>
        </div>
      </div>
    </Dialog>

    <Sidebar
      visible={isSchemaSidebarOpen}
      position="right"
      onHide={() => setIsSchemaSidebarOpen(false)}
      className="w-full md:w-[400px] lg:w-[450px]"
    >
      <div className="flex flex-col h-full overflow-hidden">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b border-borderColor pb-2">
          <Database className="text-primary" /> API Şema Sözlüğü (DTO)
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          Sorgularınızda kullanmanız (AS alias ile eşleştirmeniz) gereken güncel API alanları.
        </p>

        <InputText
          value={schemaSearchTerm}
          onChange={(e) => setSchemaSearchTerm(e.target.value)}
          placeholder="Alan veya model ara..."
          className="w-full mb-4 sticky top-0"
        />

        <div className="flex-1 overflow-y-auto pr-2 space-y-4">
          {schemaDictionary
            .filter((group: any) =>
              group.label.toLowerCase().includes(schemaSearchTerm.toLowerCase()) ||
              group.items.some((i: any) => i.label.toLowerCase().includes(schemaSearchTerm.toLowerCase()))
            )
            .map((group: any) => (
              <div key={group.groupName} className="mb-4 bg-bgAlt p-3 rounded-lg border border-borderColor shadow-sm">
                <h5 className="font-bold text-sm text-primary mb-2 border-b border-borderColor pb-1">{group.label}</h5>
                <ul className="space-y-1">
                  {group.items
                    .filter((item: any) => item.label.toLowerCase().includes(schemaSearchTerm.toLowerCase()) || schemaSearchTerm === "")
                    .map((item: any) => (
                      <li key={item.value} className="text-[11px] flex justify-between border-b border-slate-50 py-1 hover:bg-slate-50 px-1 rounded transition-colors cursor-pointer"
                        onClick={() => {
                          navigator.clipboard.writeText(item.label.split(' ')[0]);
                          toast.success(`'${item.label.split(' ')[0]}' kopyalandı!`);
                        }}>
                        <span className="font-mono font-bold text-slate-800">{item.label.split(' ')[0]}</span>
                        <span className="text-slate-400 italic">{item.type || 'string'}</span>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
        </div>
      </div>
    </Sidebar>
  </>
  );
};
